import type { CSSVariablesResolver } from "@mantine/core";

export function createBaseResolver(): CSSVariablesResolver {
  return (theme) => ({
    variables: {
      "--mantine-spacing-xxs": theme.spacing.xxs,
      "--mantine-spacing-2xl": theme.spacing["2xl"],
      "--mantine-spacing-3xl": theme.spacing["3xl"],
      "--mantine-spacing-4xl": theme.spacing["4xl"],
      "--mantine-font-size-2xl": theme.fontSizes["2xl"],
      "--mantine-font-size-3xl": theme.fontSizes["3xl"],
      "--mantine-font-size-4xl": theme.fontSizes["4xl"],
      "--mantine-font-size-5xl": theme.fontSizes["5xl"],
      "--mantine-other-spacing-mobileHeaderHeight": theme.other.spacing.mobileHeaderHeight,
      "--mantine-other-spacing-desktopHeaderHeight": theme.other.spacing.desktopHeaderHeight,
      "--mantine-other-spacing-pageGap": theme.other.spacing.pageGap,
      // Size tokens — usable as maw/w in components and CSS modules without a hook.
      "--canucci-size-max-page-width-desktop": theme.other.sizes.maxPageWidthDesktop,
      "--canucci-size-max-narrow-page-width-desktop": theme.other.sizes.maxNarrowPageWidthDesktop,
      "--canucci-size-max-page-width-mobile": theme.other.sizes.maxPageWidthMobile,
      "--canucci-size-modal-width": theme.other.sizes.modalWidth,
      "--canucci-size-content-width": theme.other.sizes.contentWidth,
      "--canucci-size-narrow-content": theme.other.sizes.narrowContent,
      // Semantic color tokens — usable in CSS modules via var(--canucci-color-*) without needing a hook.
      "--canucci-color-text": theme.other.semanticColors.text,
      "--canucci-color-inverted-text": theme.other.semanticColors.invertedText,
      "--canucci-color-accent": theme.other.semanticColors.accent,
      "--canucci-color-border-subtle": theme.other.semanticColors.borderSubtle,
      "--canucci-color-border-medium": theme.other.semanticColors.borderMedium,
      "--canucci-color-border-strong":
        theme.other.semanticColors.borderStrong ?? theme.other.semanticColors.borderMedium,
      "--canucci-color-error": theme.other.semanticColors.error,
      // Chat bubble semantic colors (optional; fall back to mantine defaults if unset)
      "--canucci-intro-bubble-bg": theme.other.semanticColors.introBubble?.background ?? "var(--mantine-color-blue-1)",
      "--canucci-intro-bubble-accent-bar":
        theme.other.semanticColors.introBubble?.accentBar ?? "var(--mantine-color-blue-6)",
      "--canucci-intro-bubble-border": theme.other.semanticColors.introBubble?.border ?? "var(--mantine-color-blue-2)",
      "--canucci-intro-bubble-label":
        theme.other.semanticColors.introBubble?.labelColor ?? "var(--mantine-color-blue-7)",
      "--canucci-complete-bubble-bg":
        theme.other.semanticColors.completeBubble?.background ?? "var(--mantine-color-green-1)",
      "--canucci-complete-bubble-accent-bar":
        theme.other.semanticColors.completeBubble?.accentBar ?? "var(--mantine-color-green-5)",
      "--canucci-complete-bubble-border":
        theme.other.semanticColors.completeBubble?.border ?? "var(--mantine-color-green-2)",
      "--canucci-complete-bubble-header":
        theme.other.semanticColors.completeBubble?.headerColor ?? "var(--mantine-color-green-8)",
      "--canucci-question-label-color":
        theme.other.semanticColors.questionLabelColor ?? theme.other.semanticColors.accent,
      "--canucci-textarea-focus-ring":
        theme.other.semanticColors.textareaFocusRing ?? "0 0 0 2px var(--mantine-color-primary-5)",
      "--canucci-color-auth-background":
        theme.other.semanticColors.authBackground ??
        theme.other.semanticColors.background ??
        "var(--mantine-color-gray-1)",
      "--canucci-color-info-soft": theme.other.semanticColors.infoSoft ?? "var(--mantine-color-blue-0)",
      "--canucci-color-text-secondary": theme.other.semanticColors.textSecondary ?? "var(--mantine-color-dimmed)",
      "--canucci-color-text-muted": theme.other.semanticColors.textMuted ?? "var(--mantine-color-dimmed)",
      "--canucci-color-fg-on-dark-dim": theme.other.semanticColors.fgOnDarkDim ?? "rgba(255, 255, 255, 0.66)",
      "--canucci-color-auth-surface-hover":
        theme.other.semanticColors.authSurfaceHover ?? "var(--mantine-color-gray-0)",
      "--canucci-auth-input-focus-ring":
        theme.other.semanticColors.textareaFocusRing ??
        "0 0 0 3px color-mix(in srgb, var(--mantine-color-accent-filled) 18%, transparent)",
      // Heading font family — mirrors theme.headings.fontFamily; usable in CSS without a hook.
      "--canucci-font-family-heading": theme.headings?.fontFamily ?? theme.fontFamily ?? "sans-serif",
    },
    light: {
      // When semanticColors.background is set (e.g. in collect-ui for the teal page bg), it takes effect here.
      // Falls back to gray-0 so apps that don't set background are unchanged.
      "--mantine-color-body": theme.other.semanticColors.background ?? "var(--mantine-color-gray-0)",
    },
    dark: {},
  });
}

export function composeResolvers(...resolvers: CSSVariablesResolver[]): CSSVariablesResolver {
  return (theme) => {
    return resolvers.reduce(
      (acc, resolver) => {
        const next = resolver(theme);
        return {
          variables: { ...acc.variables, ...next.variables },
          light: { ...acc.light, ...next.light },
          dark: { ...acc.dark, ...next.dark },
        };
      },
      { variables: {}, light: {}, dark: {} },
    );
  };
}
